'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'
import { motion } from 'framer-motion'
import { Briefcase, Building, Globe, Mail, MapPin, Phone, User } from 'lucide-react'
import { z } from 'zod'

import { UserProfile } from '../../lib/workshop-api'
import { Button } from '../ui/button'
import { Card } from '../ui/card'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select } from '../ui/select'

const userInfoSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  name: z.string().min(2, 'Name must be at least 2 characters'),
  phone: z.string().optional(),
  company: z.string().optional(),
  jobTitle: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  marketingConsent: z.boolean().default(false),
  paymentPreference: z.enum(['stripe', 'xero']).default('stripe'),
})

type UserInfoFormData = z.infer<typeof userInfoSchema>

interface UserInfoFormProps {
  onSubmit: (email: string, profile: UserProfile) => void
  initialData?: {
    email: string
    profile?: UserProfile | null
  }
  loading?: boolean
}

export function UserInfoForm({ onSubmit, initialData, loading = false }: UserInfoFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
  } = useForm<UserInfoFormData>({
    resolver: zodResolver(userInfoSchema),
    defaultValues: {
      email: initialData?.email || '',
      name: initialData?.profile?.name || '',
      phone: initialData?.profile?.phone || '',
      company: initialData?.profile?.company || '',
      jobTitle: initialData?.profile?.jobTitle || '',
      city: initialData?.profile?.city || '',
      country: initialData?.profile?.country || '',
      marketingConsent: initialData?.profile?.marketingConsent || false,
      paymentPreference: initialData?.profile?.paymentPreference || 'stripe',
    },
    mode: 'onChange',
  })

  const marketingConsent = watch('marketingConsent')

  const handleFormSubmit = (data: UserInfoFormData) => {
    const { email, ...profileData } = data
    onSubmit(email, profileData)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="border-kinetic-outline-variant/30 space-y-6 rounded-none border bg-kinetic-surface-container-low p-8 text-kinetic-on-surface shadow-none">
        <div className="space-y-2 text-center">
          <div className="bg-kinetic-surface-container-highest/60 border-kinetic-outline-variant/30 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-none border">
            <User className="h-8 w-8 text-kinetic-primary-container" />
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-kinetic-secondary-fixed">
            USER_PROFILE
          </p>
          <h2 className="font-headline text-2xl font-bold uppercase tracking-tight text-white">
            Tell us about yourself
          </h2>
          <p className="text-kinetic-on-surface-variant">
            We'll use this information to personalize your workshop experience.
          </p>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          {/* Required Information */}
          <div className="space-y-4">
            <h3 className="font-headline text-lg font-bold uppercase tracking-tight text-white">
              Required Information
            </h3>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Email */}
              <div className="space-y-2">
                <Label
                  htmlFor="email"
                  className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-kinetic-outline"
                >
                  <span className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Return_Address
                  </span>
                  <span className="text-kinetic-primary-container/50">REQ*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="EMAIL@DOMAIN.COM"
                  {...register('email')}
                  className={errors.email ? 'border-red-500' : 'font-mono'}
                />
                {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label
                  htmlFor="name"
                  className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-kinetic-outline"
                >
                  <span className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    User_Identity
                  </span>
                  <span className="text-kinetic-primary-container/50">REQ*</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="FULL_NAME"
                  {...register('name')}
                  className={errors.name ? 'border-red-500 font-mono' : 'font-mono'}
                />
                {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
              </div>
            </div>
          </div>

          {/* Optional Information */}
          <div className="space-y-4">
            <h3 className="font-headline text-lg font-bold uppercase tracking-tight text-white">
              Optional Information
            </h3>
            <p className="text-sm text-kinetic-on-surface-variant">
              Help us customize the workshop content to your background.
            </p>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Phone */}
              <div className="space-y-2">
                <Label
                  htmlFor="phone"
                  className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-kinetic-outline"
                >
                  <Phone className="h-4 w-4" />
                  Contact_Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+61 433 825 293"
                  {...register('phone')}
                  className="font-mono"
                />
              </div>

              {/* Company */}
              <div className="space-y-2">
                <Label
                  htmlFor="company"
                  className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-kinetic-outline"
                >
                  <Building className="h-4 w-4" />
                  Org_Node
                </Label>
                <Input
                  id="company"
                  type="text"
                  placeholder="COMPANY"
                  {...register('company')}
                  className="font-mono"
                />
              </div>

              {/* Job Title */}
              <div className="space-y-2">
                <Label
                  htmlFor="jobTitle"
                  className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-kinetic-outline"
                >
                  <Briefcase className="h-4 w-4" />
                  Role_Tag
                </Label>
                <Input
                  id="jobTitle"
                  type="text"
                  placeholder="JOB_TITLE"
                  {...register('jobTitle')}
                  className="font-mono"
                />
              </div>

              {/* City */}
              <div className="space-y-2">
                <Label
                  htmlFor="city"
                  className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-kinetic-outline"
                >
                  <MapPin className="h-4 w-4" />
                  City
                </Label>
                <Input
                  id="city"
                  type="text"
                  placeholder="SYDNEY"
                  {...register('city')}
                  className="font-mono"
                />
              </div>

              {/* Country */}
              <div className="space-y-2 md:col-span-2">
                <Label
                  htmlFor="country"
                  className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-kinetic-outline"
                >
                  <Globe className="h-4 w-4" />
                  Region
                </Label>
                <Input
                  id="country"
                  type="text"
                  placeholder="AU"
                  {...register('country')}
                  className="font-mono"
                />
              </div>
            </div>
          </div>

          {/* Payment Preference */}
          <div className="space-y-4">
            <h3 className="font-headline text-lg font-bold uppercase tracking-tight text-white">
              Payment Preference
            </h3>

            <div className="space-y-3">
              <label className="flex cursor-pointer items-center space-x-3">
                <input
                  type="radio"
                  value="stripe"
                  {...register('paymentPreference')}
                  className="border-kinetic-outline-variant/50 h-4 w-4 text-kinetic-primary-container focus:ring-kinetic-primary-container"
                />
                <div className="flex-1">
                  <div className="font-headline font-bold text-white">Credit/Debit Card</div>
                  <div className="text-sm text-kinetic-on-surface-variant">
                    Pay immediately with Visa, Mastercard, or American Express
                  </div>
                </div>
              </label>

              <label className="flex cursor-pointer items-center space-x-3">
                <input
                  type="radio"
                  value="xero"
                  {...register('paymentPreference')}
                  className="border-kinetic-outline-variant/50 h-4 w-4 text-kinetic-primary-container focus:ring-kinetic-primary-container"
                />
                <div className="flex-1">
                  <div className="font-headline font-bold text-white">Invoice (Xero)</div>
                  <div className="text-sm text-kinetic-on-surface-variant">
                    Receive an invoice for payment by bank transfer
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Marketing Consent */}
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="marketingConsent"
                checked={marketingConsent}
                onCheckedChange={checked => setValue('marketingConsent', !!checked)}
                className="mt-1"
              />
              <Label
                htmlFor="marketingConsent"
                className="cursor-pointer text-sm leading-relaxed text-kinetic-on-surface-variant"
              >
                I'd like to receive updates about future workshops, data engineering tips, and Cloud
                Shuttle news. You can unsubscribe at any time.
              </Label>
            </div>
          </div>

          {/* Privacy Notice */}
          <div className="bg-kinetic-surface-container-highest/40 border-kinetic-outline-variant/20 rounded-none border p-4">
            <p className="text-sm text-kinetic-on-surface-variant">
              <strong>Privacy Notice:</strong> Your information will only be used for workshop
              registration and communication. We never sell or share your data with third parties.
              View our{' '}
              <a href="/privacy" className="text-kinetic-primary-container hover:underline">
                privacy policy
              </a>
              .
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!isValid || loading}
              size="lg"
              variant="kinetic"
              className="min-w-32"
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  <span>Processing...</span>
                </div>
              ) : (
                'Continue to Workshop Selection'
              )}
            </Button>
          </div>
        </form>
      </Card>
    </motion.div>
  )
}
